import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Lock, AlertCircle, Star, HelpCircle, ArrowLeft, Trash2 } from 'lucide-react';
import {
  createFinancialForecast,
  getFinancialForecasts,
  deleteFinancialForecast
} from '../../utils/forecastApi';
import { upsertForecastAccount } from '../../utils/forecastAccountsApi';
import { getForecastDetail } from '../../utils/forecastDetailApi';
import { formatCurrencyRounded, currencySymbol } from '../../utils/currency';
import {
  calculateForecastAmount,
  driverRequiresInput,
  isAutoCalculatedDriver,
  getPreviousMonthIndex
} from '../../utils/forecastCalculations';
import DailyForecastModal from './DailyForecastModal';
import VersionHistoryDropdown from './VersionHistoryDropdown';
import DriverHelpPanel from './DriverHelpPanel';
import DataSourceBadge from './DataSourceBadge';
import MissingDataAlert from './MissingDataAlert';

const DRIVER_TYPE_OPTIONS = [
  '$ / ARN',
  '$ / ARN LM',
  '$ / ARN LY',
  '$ / ORN',
  '$ / ORN LM',
  '$ / ORN LY',
  '$ LM',
  '$POR Projection',
  'Budget',
  'LY Same Month',
  '% of Revenue',
  '% of Selected Revenue',
  'REVPAR Projection',
  'Static Entry'
];

const ForecastAccountDetail = ({
  account,
  forecastData = [],  // eslint-disable-line @typescript-eslint/no-unused-vars
  budgetData = [],    // eslint-disable-line @typescript-eslint/no-unused-vars
  orgId,
  locationId,
  currency = 'USD',
  year,
  onDataChange,
  onBack,  // Optional back button handler
  categoryDriverInfo = {},  // Category-level driver information
  forecastDriverExplanations = {}  // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
  const [selectedDriver, setSelectedDriver] = useState(account.driverType || 'Static Entry');
  const [driverInput, setDriverInput] = useState(0);
  const [savedCells, setSavedCells] = useState(new Set());
  const [detailData, setDetailData] = useState([]);
  const [forecastCosmosData, setForecastCosmosData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDailyModalOpen, setIsDailyModalOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [localInputValues, setLocalInputValues] = useState({});
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const saveTimerRef = useRef({});
  const inputRefs = useRef({});

  // Determine if this account is locked by a category driver
  const categoryInfo = account.accountGroup ? categoryDriverInfo[account.accountGroup] : null;
  const isLockedByCategory = categoryInfo && categoryInfo.affectedAccountIds.includes(account.id);
  const isControllingAccount = categoryInfo && categoryInfo.controllingAccount.id === account.id;
  const categoryDriver = categoryInfo?.driver;

  // Update selected driver when account changes
  useEffect(() => {
    console.log('Account changed - driverType:', account.driverType, 'accountId:', account.id);
    setSelectedDriver(account.driverType || 'Static Entry');
  }, [account.driverType, account.id]);

  // Fetch forecast detail data from view (for budget, actual, room stats)
  // AND forecast data from Cosmos DB
  useEffect(() => {
    const fetchData = async () => {
      if (!orgId || !locationId || !account.chartOfAccountId || !year) {
        return;
      }

      setIsLoading(true);
      try {
        // Fetch budget/actual/room data from SQL view (monthly)
        const detailDataPromise = getForecastDetail({
          orgId,
          locationId,
          chartOfAccountId: account.chartOfAccountId,
          year
        });

        // Fetch forecast data from SQL
        const forecastDataPromise = getFinancialForecasts({
          orgId,
          locationId,
          chartOfAccountId: account.chartOfAccountId
        });

        const [detailResult, forecastResult] = await Promise.all([
          detailDataPromise,
          forecastDataPromise
        ]);

        setDetailData(detailResult || []);
        setForecastCosmosData(forecastResult || []);

      } catch (error) {
        console.error('Error fetching forecast data:', error);
        setDetailData([]);
        setForecastCosmosData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [orgId, locationId, account.chartOfAccountId, year]);

  // Month definitions
  const months = useMemo(() => [
    { key: '00', label: 'Jan', index: 0 },
    { key: '01', label: 'Feb', index: 1 },
    { key: '02', label: 'Mar', index: 2 },
    { key: '03', label: 'Apr', index: 3 },
    { key: '04', label: 'May', index: 4 },
    { key: '05', label: 'Jun', index: 5 },
    { key: '06', label: 'Jul', index: 6 },
    { key: '07', label: 'Aug', index: 7 },
    { key: '08', label: 'Sep', index: 8 },
    { key: '09', label: 'Oct', index: 9 },
    { key: '10', label: 'Nov', index: 10 },
    { key: '11', label: 'Dec', index: 11 }
  ], []);

  // Process row data for the grid using combined data from SQL view and SQL forecasts
  const rowData = useMemo(() => {
    // Build a map from SQL forecast data by month
    // SQL structure: Each row is one month with forecastDate, forecastAmount, driverType, driverInput
    const cosmosDataByMonth = {};

    if (forecastCosmosData && forecastCosmosData.length > 0) {
      // Process each forecast row (one row per month)
      forecastCosmosData.forEach(forecastRow => {
        // Filter to current account and year
        if (forecastRow.chartOfAccountId !== account.chartOfAccountId) {
          return;
        }

        const forecastDate = new Date(forecastRow.forecastDate);
        const rowYear = forecastDate.getUTCFullYear();

        if (rowYear !== year) {
          return;
        }

        const monthIndex = forecastDate.getUTCMonth(); // 0-11 (use UTC to match how we save)
        const monthKey = String(monthIndex).padStart(2, '0');

        cosmosDataByMonth[monthKey] = {
          driverInput: parseFloat(forecastRow.driverInput) || 0,
          forecastAmount: parseFloat(forecastRow.forecastAmount) || 0,
          driverType: forecastRow.driverType || '',
          notes: '', // SQL financialForecast table doesn't have notes field
          forecastId: forecastRow.id,
          dailyData: [], // Daily data would come from separate table/query
          hasDailyData: false
        };
      });
    }

    if (!detailData || detailData.length === 0) {
      // Return rows with Cosmos data if available, otherwise empty
      return months.map(month => ({
        id: `${account.id}_${month.key}`,
        period: `${month.label} ${year}`,
        monthKey: month.key,
        monthIndex: month.index,
        budget: 0,
        actual: 0,
        importedRevenue: 0,
        input: cosmosDataByMonth[month.key]?.driverInput || 0,
        availableRooms: 0,
        occupiedRooms: 0,
        occupancyPercent: 0,
        forecastAmount: cosmosDataByMonth[month.key]?.forecastAmount || 0,
        forecastId: cosmosDataByMonth[month.key]?.forecastId || null
      }));
    }

    // Build a map from the SQL view data by month
    // The view returns MONTHLY aggregated data with days-with-data counts
    const dataByMonth = {};

    detailData.forEach(entry => {
      if (!entry.forecastDate) return;

      const date = new Date(entry.forecastDate);
      const monthIndex = date.getUTCMonth();
      const monthKey = String(monthIndex).padStart(2, '0');
      const cosmosData = cosmosDataByMonth[monthKey] || {};

      // Naturalize actual amounts based on account type
      // Revenue: negative values should be positive (credit balance)
      const actualAmount = entry.actualAmount || 0;
      const naturalBalanceActual = account.accountType === 'Revenue' && actualAmount < 0
        ? Math.abs(actualAmount)
        : actualAmount;

      // The view already provides monthly totals and days-with-data counts
      // Use roomStatsDaysWithData from the SQL view (COUNT DISTINCT days with room data)
      const daysWithData = entry.roomStatsDaysWithData || 0;

      dataByMonth[monthKey] = {
        budget: entry.budgetAmount || 0,
        actual: naturalBalanceActual,
        importedRevenue: entry.hotelRevenueAmount || 0,
        input: entry.savedDriverInput || cosmosData.driverInput || 0,
        availableRooms: entry.roomsAvailable || 0,
        occupiedRooms: entry.roomsOccupied || 0,
        // Use SQL view's aggregated savedForecastAmount (SUM of daily forecasts) for accurate monthly totals
        // Fall back to cosmosData.forecastAmount for backwards compatibility if SQL view not updated
        forecastAmount: entry.savedForecastAmount ?? cosmosData.forecastAmount ?? 0,
        // Hybrid Static Entry: live revenue + saved forecasts for days without revenue
        hybridStaticEntryAmount: entry.hybridStaticEntryAmount || 0,
        notes: cosmosData.notes || '',
        forecastDriver: cosmosData.driverType || selectedDriver,
        forecastId: cosmosData.forecastId || null,
        daysWithData: daysWithData,  // From SQL view - count of distinct days with room data
        otbForecastRevenue: entry.otbForecastRevenue || 0,
        rmsForecastRevenue: entry.rmsForecastRevenue || 0,
        calculatedOtbForecast: entry.calculatedOtbForecast || 0,  // SQL-calculated: Room Revenue for past + OTB for future
        // % of Revenue driver fields
        totalForecastedRevenue: entry.totalForecastedRevenue || 0,
        percentOfRevenueForecast: entry.percentOfRevenueForecast || 0
      };
    });

    // Calculate period-to-date RevPAR for REVPAR Projection driver
    let ptdRevPAR = 0;
    if (selectedDriver === 'REVPAR Projection') {
      let totalActualRevenue = 0;
      let totalAvailableRooms = 0;

      // Sum up all revenue (prefer imported revenue if available, then actual) and available rooms period-to-date
      // Don't use budget here - budget is a monthly value, not actual revenue data for RevPAR calculation
      months.forEach(month => {
        const data = dataByMonth[month.key];
        if (data) {
          // Use imported revenue if available (non-zero), otherwise fall back to actual only
          const revenueToUse = data.importedRevenue > 0 ? data.importedRevenue : data.actual;
          if (revenueToUse > 0) {
            totalActualRevenue += revenueToUse;
            totalAvailableRooms += data.availableRooms;
          }
        }
      });

      // Calculate RevPAR if we have data
      if (totalAvailableRooms > 0) {
        ptdRevPAR = totalActualRevenue / totalAvailableRooms;

      }
    }

    // Get current date for current month projection
    const now = new Date();
    const currentMonth = now.getUTCMonth();
    const currentYear = now.getUTCFullYear();

    // Create rows - one per month/period with calculations
    return months.map((month) => {
      const data = dataByMonth[month.key];
      const budget = data?.budget || 0;
      const actual = data?.actual || 0;
      const importedRevenue = data?.importedRevenue || 0;
      const input = data?.input || driverInput || 0;
      const availableRooms = data?.availableRooms || 0;
      const occupiedRooms = data?.occupiedRooms || 0;
      const daysWithData = data?.daysWithData || 0;

      // Calculate occupancy percentage
      const occupancyPercent = availableRooms > 0
        ? (occupiedRooms / availableRooms) * 100
        : 0;

      // Get previous month data for $ LM driver
      const previousMonthIndex = getPreviousMonthIndex(month.index);
      const previousMonthKey = String(previousMonthIndex).padStart(2, '0');
      const previousMonthData = dataByMonth[previousMonthKey];

      // Determine if this is the current month or a past month
      const isCurrentMonth = (month.index === currentMonth && year === currentYear);
      const isPastMonth = (year < currentYear) || (year === currentYear && month.index < currentMonth);

      // Get OTB/RMS forecast revenue data
      const otbForecastRevenue = data?.otbForecastRevenue || 0;
      const rmsForecastRevenue = data?.rmsForecastRevenue || 0;

      // Calculate forecast amount based on driver
      let forecastAmount;
      let calculatedForecast;

      // Special handling for OTB Forecast driver - use SQL-calculated value
      if (selectedDriver === 'OTB Forecast') {
        // Use the pre-calculated OTB forecast from SQL view
        // This already accounts for room revenue on past days + OTB on today/future
        forecastAmount = data?.calculatedOtbForecast || 0;
      } else if (isPastMonth) {
        // For past months with other drivers, use actual amount
        forecastAmount = actual;
      } else {
        // For current and future months, apply forecasting logic
        if (selectedDriver === 'RMS Forecast') {
          // Use RMS forecast revenue from hotelOccupancy table
          calculatedForecast = rmsForecastRevenue;
        } else if (selectedDriver === 'REVPAR Projection') {
          // Determine which revenue source to use based on data availability
          // Use imported revenue if available (non-zero), otherwise fall back to actual
          // Budget is handled separately since it's already a monthly value (no extrapolation needed)
          const hasRevenueData = importedRevenue > 0 || actual > 0;
          const revenueToUse = importedRevenue > 0 ? importedRevenue : actual;

          if (isCurrentMonth && daysWithData > 0 && hasRevenueData) {
            // Current month with partial data - project to end of month
            const daysInMonth = new Date(year, month.index + 1, 0).getDate();

            if (daysWithData < daysInMonth) {
              // Project revenue to full month based on daily average
              // Formula: (revenueToUse / daysWithData) × daysInMonth
              const avgDailyRevenue = revenueToUse / daysWithData;
              calculatedForecast = avgDailyRevenue * daysInMonth;
            } else {
              // Full month of data
              calculatedForecast = revenueToUse;
            }
          } else if (ptdRevPAR > 0 && availableRooms > 0) {
            // Future month - use PTD RevPAR
            calculatedForecast = ptdRevPAR * availableRooms;
          } else if (input > 0 && availableRooms > 0) {
            // Fallback to manual input if no actual data yet
            calculatedForecast = input * availableRooms;
          } else if (budget > 0) {
            // No revenue data yet - use budget directly as baseline (don't extrapolate, it's already monthly)
            calculatedForecast = budget;
          } else {
            calculatedForecast = 0;
          }
        } else if (selectedDriver === '% of Revenue') {
          // Use pre-calculated % of Revenue forecast from SQL
          // SQL calculates: (driverInput / 100) * totalForecastedRevenue
          calculatedForecast = data?.percentOfRevenueForecast || 0;
        } else if (selectedDriver === 'Static Entry' || !selectedDriver) {
          // For Static Entry or no driver:
          // Priority: forecastAmount (monthly override) > hybridStaticEntryAmount (live revenue)
          // When user enters a monthly static entry, it should override live revenue
          // hybridStaticEntryAmount only used when no saved forecast exists (shows live revenue)
          calculatedForecast = data?.forecastAmount ?? data?.hybridStaticEntryAmount ?? 0;
        } else {
          // Use standard calculation for other drivers
          const daysInMonth = new Date(year, month.index + 1, 0).getDate();

          calculatedForecast = calculateForecastAmount({
            driverType: selectedDriver,
            input: input,
            budget: budget,
            actual: actual,
            availableRooms: availableRooms,
            occupiedRooms: occupiedRooms,
            previousMonthActual: previousMonthData?.actual || 0,
            previousMonthForecast: previousMonthData?.forecastAmount || 0,
            previousMonthAvailableRooms: previousMonthData?.availableRooms || 0,
            previousMonthOccupiedRooms: previousMonthData?.occupiedRooms || 0,
            totalRevenue: 0, // TODO: Fetch revenue data if using % of Revenue driver
            daysWithData: daysWithData,
            daysInMonth: daysInMonth
          });
        }

        // Use calculated forecast if driver is auto-calculating, otherwise use saved value
        forecastAmount = calculatedForecast !== null
          ? calculatedForecast
          : (data?.forecastAmount || 0);
      }

      if (selectedDriver === 'Static Entry' || selectedDriver === 'REVPAR Projection') {

      }

      return {
        id: `${account.id}_${month.key}`,
        period: `${month.label} ${year}`,
        monthKey: month.key,
        monthIndex: month.index,
        budget: budget,
        actual: actual,
        importedRevenue: importedRevenue,
        input: input,
        availableRooms: availableRooms,
        occupiedRooms: occupiedRooms,
        occupancyPercent: occupancyPercent,
        forecastAmount: forecastAmount,
        notes: data?.notes || '',
        forecastId: data?.forecastId || null,
        daysWithData: daysWithData
      };
    });
  }, [detailData, forecastCosmosData, account.id, account.accountType, year, months, selectedDriver, driverInput]);

  // Calculate totals row for the grid
  const totalsRow = useMemo(() => {
    if (!rowData || rowData.length === 0) return null;

    const totals = {
      id: 'totals-row',
      period: 'TOTAL',
      budget: rowData.reduce((sum, row) => sum + (row.budget || 0), 0),
      actual: rowData.reduce((sum, row) => sum + (row.actual || 0), 0),
      importedRevenue: rowData.reduce((sum, row) => sum + (row.importedRevenue || 0), 0),
      input: '', // Don't sum input values
      availableRooms: rowData.reduce((sum, row) => sum + (row.availableRooms || 0), 0),
      occupiedRooms: rowData.reduce((sum, row) => sum + (row.occupiedRooms || 0), 0),
      occupancyPercent: '', // Don't calculate average occupancy
      forecastAmount: rowData.reduce((sum, row) => sum + (row.forecastAmount || 0), 0),
      forecastId: null
    };

    return [totals];
  }, [rowData]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalRevenue = rowData.reduce((sum, row) => sum + (row.forecastAmount || 0), 0);
    const totalAvailableRooms = rowData.reduce((sum, row) => sum + (row.availableRooms || 0), 0);
    const totalOccupiedRooms = rowData.reduce((sum, row) => sum + (row.occupiedRooms || 0), 0);
    const avgRevenuePerMonth = totalRevenue / 12;
    const avgAvailableRooms = totalAvailableRooms / 12;
    const avgOccupiedRooms = totalOccupiedRooms / 12;

    return {
      totalRevenue,
      totalAvailableRooms,
      totalOccupiedRooms,
      avgRevenuePerMonth,
      avgAvailableRooms,
      avgOccupiedRooms
    };
  }, [rowData]);

  const formatCurrency = useCallback((value) => {
    if (!value && value !== 0) return `${currencySymbol(currency)}0`;
    return formatCurrencyRounded(value, currency);
  }, [currency]);

  // Handle driver type change
  const handleDriverChange = async (newDriverType) => {
    try {
      setSelectedDriver(newDriverType);
      setDriverInput(0); // Reset driver input when changing driver
      setLocalInputValues({}); // Clear all local input values

      // Delete existing forecast data from SQL before changing driver
      // Find all forecast rows for this account/year and delete them
      const { deleteFinancialForecast } = await import('../../utils/forecastApi');

      const forecastRowsToDelete = forecastCosmosData.filter(row =>
        row.chartOfAccountId === account.chartOfAccountId &&
        new Date(row.forecastDate).getUTCFullYear() === year
      );

      if (forecastRowsToDelete.length > 0) {
        console.log(`Deleting ${forecastRowsToDelete.length} existing forecast rows`);
        try {
          // Delete each forecast row
          await Promise.all(
            forecastRowsToDelete.map(row => deleteFinancialForecast(row.id))
          );
          console.log('Successfully deleted old forecast data');
        } catch (deleteError) {
          console.error('Error deleting old forecast data:', deleteError);
          // Continue anyway - the new driver will overwrite
        }
      }

      // Update the forecast account with new driver type
      const payload = {
        orgId,
        locationId,
        chartOfAccountId: account.chartOfAccountId,
        forecastDriver: newDriverType,
        inputMode: 'monthly' // Default mode
      };

      console.log('Upserting forecast account with payload:', payload);
      await upsertForecastAccount(payload);
      console.log('Updated forecast account driver type to:', newDriverType);

      // Clear the forecast data since we're changing drivers
      // This will force recalculation using the new driver logic
      setForecastCosmosData([]);
      console.log('Cleared forecast data to force recalculation with new driver');

      // Notify parent to reload the forecastAccounts data
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error updating driver type:', error);
    }
  };

  // Debounced save function - saves to API after user stops typing
  const debouncedSave = useCallback(async (cellKey, payload) => {
    try {
      console.log('Saving forecast data to SQL:', payload);

      // Use createFinancialForecast - the SQL endpoint handles upsert via delete-then-insert
      const result = await createFinancialForecast(payload);
      console.log('SQL save result:', result);

      // Optimistically update the local forecast data
      setForecastCosmosData(prev => {
        const forecastDate = new Date(payload.forecastDate);
        const monthIndex = forecastDate.getUTCMonth();

        // Find existing row for this month or create new one
        const existingIndex = prev.findIndex(row =>
          row.chartOfAccountId === account.chartOfAccountId &&
          new Date(row.forecastDate).getUTCMonth() === monthIndex &&
          new Date(row.forecastDate).getUTCFullYear() === year
        );

        const updatedRow = {
          id: result?.id || (existingIndex >= 0 ? prev[existingIndex].id : null),
          chartOfAccountId: account.chartOfAccountId,
          forecastDate: payload.forecastDate,
          driverInput: payload.driverInput,
          forecastAmount: payload.forecastAmount,
          driverType: payload.driverType
        };

        if (existingIndex >= 0) {
          // Update existing row
          const updated = [...prev];
          updated[existingIndex] = updatedRow;
          return updated;
        } else {
          // Add new row
          return [...prev, updatedRow];
        }
      });

      // Remove green feedback after delay
      setTimeout(() => {
        setSavedCells(prev => {
          const updated = new Set(prev);
          updated.delete(cellKey);
          return updated;
        });
      }, 500);

      // Only notify parent occasionally, not on every keystroke
      if (onDataChange && Math.random() < 0.1) { // 10% chance to notify
        onDataChange();
      }
    } catch (error) {
      console.error('Error saving cell:', error);
      setSavedCells(prev => {
        const updated = new Set(prev);
        updated.delete(cellKey);
        return updated;
      });
    }
  }, [account, year, onDataChange]);

  // Handle cell editing in the custom table with debouncing
  const handleCellEdit = useCallback((row, field, newValue) => {
    const monthIndex = row.monthIndex;
    const forecastDate = new Date(Date.UTC(year, monthIndex, 1)).toISOString();
    const cellKey = `${row.id}_${field}`;

    // Update local state immediately for responsive UI
    setLocalInputValues(prev => ({
      ...prev,
      [cellKey]: newValue
    }));

    // Show green feedback
    setSavedCells(prev => new Set([...prev, cellKey]));

    let finalInput = row.input;
    let finalForecastAmount = row.forecastAmount;

    // Update the changed field
    if (field === 'notes') {
      // Notes field - no action needed as SQL table doesn't have notes column
    } else if (field === 'input') {
      finalInput = newValue;

      // For Static Entry, input value becomes the forecast amount
      if (selectedDriver === 'Static Entry') {
        finalForecastAmount = finalInput;

        // Update the forecast amount in local state immediately for real-time display
        const forecastCellKey = `${row.id}_forecastAmount`;
        setLocalInputValues(prev => ({
          ...prev,
          [forecastCellKey]: finalForecastAmount
        }));

        // Show green feedback on forecast amount cell
        setSavedCells(prev => new Set([...prev, forecastCellKey]));
        setTimeout(() => {
          setSavedCells(prev => {
            const updated = new Set(prev);
            updated.delete(forecastCellKey);
            return updated;
          });
        }, 500);
      } else if (selectedDriver === '% of Revenue') {
        // For % of Revenue, don't calculate forecastAmount on client side
        // The SQL will calculate it based on totalForecastedRevenue
        // Just save the driverInput (percentage) and let SQL handle the rest
        // Set forecastAmount to null/0 - SQL percentOfRevenueForecast will be used for display
        finalForecastAmount = 0;
      } else {
        // Get previous month data for $ LM driver
        const previousMonthIndex = getPreviousMonthIndex(monthIndex);
        const previousMonthKey = String(previousMonthIndex).padStart(2, '0');
        const previousMonthRow = rowData.find(r => r.monthKey === previousMonthKey);

        // Calculate days in month for pace-based projections
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

        // Calculate the new forecast amount
        const calculatedForecast = calculateForecastAmount({
          driverType: selectedDriver,
          input: finalInput,
          budget: row.budget,
          actual: row.actual,
          availableRooms: row.availableRooms,
          occupiedRooms: row.occupiedRooms,
          previousMonthActual: previousMonthRow?.actual || 0,
          previousMonthForecast: previousMonthRow?.forecastAmount || 0,
          previousMonthAvailableRooms: previousMonthRow?.availableRooms || 0,
          previousMonthOccupiedRooms: previousMonthRow?.occupiedRooms || 0,
          totalRevenue: 0,
          daysWithData: row.daysWithData || 0,
          daysInMonth: daysInMonth
        });

        // Use calculated value if driver requires calculation
        if (calculatedForecast !== null) {
          finalForecastAmount = calculatedForecast;

          // Update the forecast amount in local state immediately for real-time display
          const forecastCellKey = `${row.id}_forecastAmount`;
          setLocalInputValues(prev => ({
            ...prev,
            [forecastCellKey]: finalForecastAmount
          }));

          // Show green feedback on forecast amount cell
          setSavedCells(prev => new Set([...prev, forecastCellKey]));
          setTimeout(() => {
            setSavedCells(prev => {
              const updated = new Set(prev);
              updated.delete(forecastCellKey);
              return updated;
            });
          }, 500);
        }
      }
    } else if (field === 'forecastAmount') {
      finalForecastAmount = newValue;
    }

    // Create payload matching SQL schema
    const payload = {
      orgId,
      locationId,
      chartOfAccountId: account.chartOfAccountId,
      forecastDate,
      driverType: selectedDriver || 'Static Entry',
      driverInput: finalInput,
      forecastAmount: finalForecastAmount
      // Note: SQL financialForecast table doesn't have a notes field
    };

    // Clear existing timer for this cell
    if (saveTimerRef.current[cellKey]) {
      clearTimeout(saveTimerRef.current[cellKey]);
    }

    // Set new timer to save after 800ms of no typing
    saveTimerRef.current[cellKey] = setTimeout(() => {
      debouncedSave(cellKey, payload);
      delete saveTimerRef.current[cellKey];
    }, 800);
  }, [account, orgId, locationId, year, selectedDriver, rowData, debouncedSave]);

  // Check if forecast should be editable (only for Static Entry or empty driver)
  const isForecastEditable = !isAutoCalculatedDriver(selectedDriver);
  const isInputEditable = driverRequiresInput(selectedDriver);

  // Handle Enter key to move to next input
  const handleKeyDown = useCallback((e, currentRow, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Find the current row index
      const currentRowIndex = rowData.findIndex(r => r.id === currentRow.id);

      // Determine the next field and row
      let nextRow = currentRow;
      let nextField = currentField;
      let nextRowIndex = currentRowIndex;

      // Logic for moving to next field
      if (currentField === 'input' && isInputEditable) {
        // Move to next row's input field
        nextRowIndex = currentRowIndex + 1;
        if (nextRowIndex < rowData.length) {
          nextRow = rowData[nextRowIndex];
          nextField = 'input';
        } else {
          return; // Already at last row
        }
      } else if (currentField === 'forecastAmount' && isForecastEditable) {
        // Move to notes field in same row
        nextField = 'notes';
      } else if (currentField === 'notes') {
        // Move to next row's first editable field
        nextRowIndex = currentRowIndex + 1;
        if (nextRowIndex < rowData.length) {
          nextRow = rowData[nextRowIndex];
          nextField = isInputEditable ? 'input' : (isForecastEditable ? 'forecastAmount' : 'notes');
        } else {
          return; // Already at last row
        }
      }

      // Focus the next input
      const nextKey = `${nextRow.id}_${nextField}`;
      if (inputRefs.current[nextKey]) {
        inputRefs.current[nextKey].focus();
      }
    }
  }, [rowData, isInputEditable, isForecastEditable]);

  // Handle modal save
  const handleDailyModalSave = useCallback(async () => {
    // Refetch Cosmos DB data after daily forecast is saved
    try {
      const forecastResult = await getFinancialForecasts({
        orgId,
        locationId,
        chartOfAccountId: account.chartOfAccountId
      });
      setForecastCosmosData(forecastResult || []);

      // Also call parent's onDataChange if provided
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error refetching forecast data:', error);
    }
  }, [orgId, locationId, account.chartOfAccountId, onDataChange]);

  // Handle version restore
  const handleVersionRestore = useCallback(async () => {
    // Refetch Cosmos DB data after version restore
    try {
      const forecastResult = await getFinancialForecasts({
        orgId,
        locationId,
        chartOfAccountId: account.chartOfAccountId
      });
      setForecastCosmosData(forecastResult || []);

      // Also call parent's onDataChange if provided
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error refetching forecast data:', error);
    }
  }, [orgId, locationId, account.chartOfAccountId, onDataChange]);

  // Handle clear forecast - deletes all forecast entries and resets driver
  const handleClearForecast = useCallback(async () => {
    setIsClearing(true);
    try {
      // Find all forecast rows for this account/year and delete them
      const forecastRowsToDelete = forecastCosmosData.filter(row =>
        row.chartOfAccountId === account.chartOfAccountId &&
        new Date(row.forecastDate).getUTCFullYear() === year
      );

      if (forecastRowsToDelete.length > 0) {
        console.log(`Clearing ${forecastRowsToDelete.length} forecast rows`);
        await Promise.all(
          forecastRowsToDelete.map(row => deleteFinancialForecast(row.id))
        );
        console.log('Successfully cleared forecast data');
      }

      // Reset the forecast account driver to empty
      const payload = {
        orgId,
        locationId,
        chartOfAccountId: account.chartOfAccountId,
        forecastDriver: '', // Reset to blank
        inputMode: 'monthly'
      };

      await upsertForecastAccount(payload);
      console.log('Reset forecast driver to blank');

      // Clear local state
      setSelectedDriver('Static Entry');
      setDriverInput(0);
      setLocalInputValues({});
      setForecastCosmosData([]);

      // Close confirmation dialog
      setShowClearConfirm(false);

      // Notify parent to reload data
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error clearing forecast:', error);
    } finally {
      setIsClearing(false);
    }
  }, [forecastCosmosData, account.chartOfAccountId, year, orgId, locationId, onDataChange]);

  return (
    <div className="flex flex-col bg-white" style={{
      height: 'calc(100vh - 280px)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div className="px-6 py-3 flex-shrink-0 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to account list"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
            )}
            <div className="flex items-baseline gap-3">
              <h2 className="text-xl font-bold text-gray-800">{account.accountName}</h2>
              <p className="text-sm text-gray-500">#{account.accountNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowClearConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
              title="Clear all forecast data for this account"
            >
              <Trash2 size={14} />
              Clear Forecast
            </button>
            <VersionHistoryDropdown
              orgId={orgId}
              locationId={locationId}
              chartOfAccountId={account.chartOfAccountId}
              year={year}
              onRestore={handleVersionRestore}
            />
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
              account.accountType === 'Revenue'
                ? 'text-green-800 bg-green-100' :
              account.accountType === 'Expense'
                ? 'text-red-800 bg-red-100' :
                'text-gray-800 bg-gray-100'
            }`}>
              {account.accountType}
            </span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-3">
          <div className="p-3 bg-orange-50 rounded-lg">
            <div className="text-xs font-medium text-orange-600 mb-1">Available Rooms</div>
            <div className="text-xl font-bold text-orange-800">
              {summary.totalAvailableRooms.toLocaleString()}
            </div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="text-xs font-medium text-blue-600 mb-1">Occupied Rooms</div>
            <div className="text-xl font-bold text-blue-800">
              {summary.totalOccupiedRooms.toLocaleString()}
            </div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <div className="text-xs font-medium text-green-600 mb-1">Total Forecast</div>
            <div className="text-xl font-bold text-green-800">
              {formatCurrency(summary.totalRevenue)}
            </div>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg">
            <div className="text-xs font-medium text-purple-600 mb-1">Avg/Month</div>
            <div className="text-xl font-bold text-purple-800">
              {formatCurrency(summary.avgRevenuePerMonth)}
            </div>
          </div>
        </div>

        {/* Category Driver Warning Banner */}
        {isLockedByCategory && !isControllingAccount && (
          <div className="mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-start gap-3">
              <Lock className="text-blue-600 mt-0.5 flex-shrink-0" size={18} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-blue-900">
                    Category-Level Driver Active
                  </span>
                </div>
                <p className="text-xs text-blue-700 mb-2">
                  This account is part of the <span className="font-semibold">{account.accountGroup}</span> category,
                  which is using <span className="font-semibold">{categoryDriver}</span> as a category-wide driver.
                  All accounts in this category share the same forecast driver.
                </p>
                <div className="flex items-center gap-1.5 text-xs text-blue-600">
                  <Star size={12} className="text-blue-500" />
                  <span>
                    Controlled by: {categoryInfo.controllingAccount.accountNumber} - {categoryInfo.controllingAccount.accountName}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controlling Account Badge */}
        {isControllingAccount && (
          <div className="mb-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
            <div className="flex items-start gap-3">
              <Star className="text-orange-600 mt-0.5 flex-shrink-0 fill-orange-600" size={18} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-orange-900">
                    Controlling Account for {account.accountGroup} Category
                  </span>
                </div>
                <p className="text-xs text-orange-700 mb-2">
                  The forecast driver you select here (<span className="font-semibold">{categoryDriver}</span>) applies
                  to all <span className="font-semibold">{categoryInfo.affectedAccountIds.length}</span> accounts in
                  the {account.accountGroup} category.
                </p>
                <div className="flex items-center gap-1.5 text-xs text-orange-600">
                  <AlertCircle size={12} />
                  <span>
                    Changing this driver will affect all accounts in this category
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Forecast Driver Selection */}
        <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border border-gray-200">
          <label className="text-sm font-semibold text-gray-700 whitespace-nowrap flex items-center gap-2">
            {isLockedByCategory && !isControllingAccount && (
              <Lock size={14} className="text-blue-600" />
            )}
            Forecast Driver:
          </label>
          <select
            value={selectedDriver}
            onChange={(e) => handleDriverChange(e.target.value)}
            disabled={isLockedByCategory && !isControllingAccount}
            className={`w-48 px-3 py-1.5 rounded-md text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all ${
              isLockedByCategory && !isControllingAccount
                ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-white border-gray-300'
            }`}
            title={isLockedByCategory && !isControllingAccount
              ? `This driver is locked by category-level setting (${categoryDriver})`
              : ''
            }
          >
            {DRIVER_TYPE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {/* Help Button */}
          <button
            onClick={() => setShowHelpPanel(!showHelpPanel)}
            className={`p-1.5 rounded-md transition-colors ${
              showHelpPanel
                ? 'bg-blue-100 text-blue-700'
                : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
            }`}
            title="Show driver help and troubleshooting"
          >
            <HelpCircle size={18} />
          </button>

          {isLockedByCategory && !isControllingAccount && (
            <div className="flex items-center gap-1.5 text-xs text-blue-600">
              <AlertCircle size={12} />
              <span>Locked by category driver</span>
            </div>
          )}

          {/* Driver Input Field - Hidden as requested */}
          {false && driverRequiresInput(selectedDriver) && (
            <>
              <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                {selectedDriver === '% of Revenue' ? 'Percentage (%)' : 'Input Value ($)'}:
              </label>
              <input
                type="number"
                value={selectedDriver === '% of Revenue' ? (driverInput * 100).toFixed(2) : driverInput}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if (selectedDriver === '% of Revenue') {
                    setDriverInput(value / 100);
                  } else {
                    setDriverInput(value);
                  }
                }}
                placeholder="0"
                className="w-32 bg-white px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                step={selectedDriver === '% of Revenue' ? '0.01' : '1'}
                min="0"
              />
            </>
          )}
        </div>

        {/* Driver Help Panel */}
        <DriverHelpPanel
          isOpen={showHelpPanel}
          onClose={() => setShowHelpPanel(false)}
          driverType={selectedDriver}
          rowData={selectedMonth !== null ? rowData[selectedMonth] : rowData[0]}
          accountType={account.accountType}
        />

        {/* Missing Data Alerts */}
        <MissingDataAlert
          account={account}
          rowData={rowData}
          selectedDriver={selectedDriver}
        />
      </div>

      {/* Custom Table */}
      <div className="flex-1 px-6 pb-4 pt-2" style={{ minHeight: 0, overflow: 'auto' }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-30">Period</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Budget</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Actual</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Imported Revenue</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Available Rooms</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Occupied Rooms</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Occupancy %</th>
                    {isInputEditable && (
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Input</th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Forecast Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rowData.map((row) => {
                    const now = new Date();
                    const currentMonth = now.getUTCMonth();
                    const currentYear = now.getUTCFullYear();
                    const isPast = year < currentYear || (year === currentYear && row.monthIndex < currentMonth);
                    const isPresent = year === currentYear && row.monthIndex === currentMonth;
                    const cellKey = `${row.id}`;

                    return (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        {/* Period */}
                        <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-sm font-medium ${
                                (selectedDriver === 'Static Entry' || selectedDriver === 'OTB Forecast' || selectedDriver === 'RMS Forecast') ? 'text-orange-600 cursor-pointer hover:underline' :
                                isPast ? 'text-gray-500' : isPresent ? 'text-blue-600 font-bold' : 'text-gray-700'
                              }`}
                              onClick={() => {
                                if (selectedDriver === 'Static Entry' || selectedDriver === 'OTB Forecast' || selectedDriver === 'RMS Forecast') {
                                  setSelectedMonth(row.monthIndex);
                                  setIsDailyModalOpen(true);
                                }
                              }}
                            >
                              {row.period}
                            </span>
                            {isPast && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-100 rounded">
                                Pa
                              </span>
                            )}
                            {isPresent && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium text-blue-600 bg-blue-50 rounded">
                                Pr
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Budget */}
                        <td className="px-4 py-3 text-right text-sm text-gray-700 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            <span>{formatCurrency(row.budget)}</span>
                            {row.budget > 0 && (
                              <DataSourceBadge
                                source="Budget"
                                showTooltip={true}
                              />
                            )}
                          </div>
                        </td>

                        {/* Actual */}
                        <td className="px-4 py-3 text-right text-sm text-gray-700 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            <span>{formatCurrency(row.actual)}</span>
                            {row.actual > 0 && (
                              <DataSourceBadge
                                source="PMS"
                                showTooltip={true}
                                importDate={row.actualImportDate}
                              />
                            )}
                          </div>
                        </td>

                        {/* Imported Revenue */}
                        <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            <span>{formatCurrency(row.importedRevenue)}</span>
                            {row.importedRevenue > 0 && (
                              <DataSourceBadge
                                source="PMS"
                                showTooltip={true}
                                importDate={row.revenueImportDate}
                                completeness={row.daysWithData > 0 ? (row.daysWithData / new Date(year, row.monthIndex + 1, 0).getDate()) * 100 : 0}
                              />
                            )}
                          </div>
                        </td>

                        {/* Available Rooms */}
                        <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            <span>{row.availableRooms.toLocaleString()}</span>
                            {row.availableRooms > 0 && (
                              <DataSourceBadge
                                source="PMS"
                                showTooltip={true}
                                importDate={row.roomImportDate}
                                completeness={row.daysWithData > 0 ? (row.daysWithData / new Date(year, row.monthIndex + 1, 0).getDate()) * 100 : 0}
                              />
                            )}
                          </div>
                        </td>

                        {/* Occupied Rooms */}
                        <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            <span>{row.occupiedRooms.toLocaleString()}</span>
                            {row.occupiedRooms > 0 && (
                              <DataSourceBadge
                                source="PMS"
                                showTooltip={true}
                                importDate={row.roomImportDate}
                                completeness={row.daysWithData > 0 ? (row.daysWithData / new Date(year, row.monthIndex + 1, 0).getDate()) * 100 : 0}
                              />
                            )}
                          </div>
                        </td>

                        {/* Occupancy % */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-blue-500 h-1.5 rounded-full"
                                style={{ width: `${Math.min(100, row.occupancyPercent)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600 w-10 text-right">{row.occupancyPercent.toFixed(1)}%</span>
                          </div>
                        </td>

                        {/* Input (conditional) */}
                        {isInputEditable && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <input
                              ref={(el) => inputRefs.current[`${cellKey}_input`] = el}
                              type="number"
                              value={localInputValues[`${cellKey}_input`] !== undefined ? localInputValues[`${cellKey}_input`] : row.input}
                              onChange={(e) => handleCellEdit(row, 'input', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, row, 'input')}
                              onFocus={() => {
                                const currentValue = localInputValues[`${cellKey}_input`] !== undefined ? localInputValues[`${cellKey}_input`] : row.input;
                                if (parseFloat(currentValue) === 0) {
                                  setLocalInputValues(prev => ({
                                    ...prev,
                                    [`${cellKey}_input`]: ''
                                  }));
                                }
                              }}
                              onBlur={(e) => {
                                if (e.target.value === '') {
                                  handleCellEdit(row, 'input', 0);
                                }
                              }}
                              className={`w-20 px-2 py-1 text-sm text-right border rounded focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                savedCells.has(`${cellKey}_input`) ? 'bg-green-100' : 'bg-orange-50 border-orange-200'
                              }`}
                              step="0.01"
                              min="0"
                            />
                          </td>
                        )}

                        {/* Forecast Amount */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            {isForecastEditable ? (
                              <input
                                ref={(el) => inputRefs.current[`${cellKey}_forecastAmount`] = el}
                                type="number"
                                value={localInputValues[`${cellKey}_forecastAmount`] !== undefined ? localInputValues[`${cellKey}_forecastAmount`] : row.forecastAmount}
                                onChange={(e) => handleCellEdit(row, 'forecastAmount', parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => handleKeyDown(e, row, 'forecastAmount')}
                                onFocus={() => {
                                  const currentValue = localInputValues[`${cellKey}_forecastAmount`] !== undefined ? localInputValues[`${cellKey}_forecastAmount`] : row.forecastAmount;
                                  if (parseFloat(currentValue) === 0) {
                                    setLocalInputValues(prev => ({
                                      ...prev,
                                      [`${cellKey}_forecastAmount`]: ''
                                    }));
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    handleCellEdit(row, 'forecastAmount', 0);
                                  }
                                }}
                                className={`w-28 px-2 py-1 text-sm font-bold text-right text-green-800 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                  savedCells.has(`${cellKey}_forecastAmount`) ? 'bg-green-100' : 'bg-white border-gray-200'
                                }`}
                                step="0.01"
                                min="0"
                              />
                            ) : (
                              <span className="text-sm font-bold text-green-800">
                                {formatCurrency(localInputValues[`${cellKey}_forecastAmount`] !== undefined ? localInputValues[`${cellKey}_forecastAmount`] : row.forecastAmount)}
                              </span>
                            )}
                            {row.forecastAmount > 0 && (
                              <DataSourceBadge
                                source={
                                  selectedDriver === 'Static Entry' ? 'Manual' :
                                  selectedDriver === 'OTB Forecast' ? 'OTB' :
                                  selectedDriver === 'RMS Forecast' ? 'RMS' :
                                  selectedDriver === 'Budget' ? 'Budget' :
                                  'Manual'
                                }
                                showTooltip={true}
                              />
                            )}
                          </div>
                        </td>

                        {/* Notes */}
                        <td className="px-4 py-3">
                          <input
                            ref={(el) => inputRefs.current[`${cellKey}_notes`] = el}
                            type="text"
                            value={localInputValues[`${cellKey}_notes`] !== undefined ? localInputValues[`${cellKey}_notes`] : (row.notes || '')}
                            onChange={(e) => handleCellEdit(row, 'notes', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, row, 'notes')}
                            placeholder="Add notes..."
                            className={`w-full px-2 py-1 text-sm text-gray-600 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                              savedCells.has(`${cellKey}_notes`) ? 'bg-green-100' : 'bg-white border-gray-200'
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals Row */}
                  {totalsRow && totalsRow[0] && (
                    <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 sticky left-0 bg-gray-100 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">TOTAL</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(totalsRow[0].budget)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(totalsRow[0].actual)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(totalsRow[0].importedRevenue)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{totalsRow[0].availableRooms.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900">{totalsRow[0].occupiedRooms.toLocaleString()}</td>
                      <td className="px-4 py-3"></td>
                      {isInputEditable && <td className="px-4 py-3"></td>}
                      <td className="px-4 py-3 text-right text-sm font-bold text-green-900">{formatCurrency(totalsRow[0].forecastAmount)}</td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Daily Forecast Modal */}
      {selectedMonth !== null && (
        <DailyForecastModal
          isOpen={isDailyModalOpen}
          onClose={() => {
            setIsDailyModalOpen(false);
            setSelectedMonth(null);
          }}
          account={account}
          month={selectedMonth}
          year={year}
          orgId={orgId}
          locationId={locationId}
          currency={currency}
          selectedDriver={selectedDriver}
          onSave={handleDailyModalSave}
        />
      )}

      {/* Clear Forecast Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Clear Forecast Data?</h3>
            </div>
            <p className="text-gray-600 mb-6">
              This will permanently delete all forecast entries for <span className="font-semibold">{account.accountName}</span> and reset the forecast driver to blank. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={isClearing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClearForecast}
                disabled={isClearing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isClearing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Clear Forecast
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastAccountDetail;
